import React, { useMemo, useState } from 'react';
import { Box, Button, ButtonBase, Collapse, IconButton } from '@mui/material';
import { Add as AddIcon, ChevronRight as OpenIcon, DragIndicator as DragIcon, MoreVert as MoreIcon } from '@mui/icons-material';
import { DndContext, DragEndEvent, PointerSensor, closestCenter, useSensor, useSensors } from '@dnd-kit/core';
import { SortableContext, arrayMove, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { useTranslation } from 'react-i18next';
import { tokens } from '../../theme/tokens';
import { formatMoney } from '../../theme/chart';
import { localTodayIso, sumByCurrency } from '../../utils/bills';
import { goalProgress } from '../../utils/goals';
import { Goal, GoalContribution } from '../../services/goalApi';
import { CardTitle } from '../common/CardTitle';
import { goalOutlook } from './goalOutlook';
import { monthKeyOf } from './goalMonths';

interface Props {
  goals: Goal[];
  archived: Goal[];
  trails: Record<string, GoalContribution[]>;
  /** the goal open in the panel, highlighted when both are on screen */
  selectedId: string | null;
  highlightSelected: boolean;
  onSelect: (goal: Goal) => void;
  now: Date;
  canCreate: boolean;
  onCreate: () => void;
  canReorder: boolean;
  onReorder: (goalIds: string[]) => void;
  canModify: (goal: Goal) => boolean;
  onUnarchive: (goal: Goal) => void;
  onMenu: (anchor: HTMLElement, goal: Goal) => void;
}

type Tone = 'ok' | 'warn' | 'muted';
const toneColor: Record<Tone, string> = { ok: tokens.accent, warn: tokens.warn, muted: tokens.ink3 };

const capitalize = (text: string) => text.charAt(0).toLocaleUpperCase() + text.slice(1);

/** Places one row in the drag context; only the handle starts a drag. */
const SortableRow: React.FC<{
  id: string;
  disabled: boolean;
  children: (handle: { attributes: object; listeners: object | undefined }) => React.ReactNode;
}> = ({ id, disabled, children }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id, disabled });
  return (
    <Box
      component='li'
      ref={setNodeRef}
      style={{ transform: CSS.Transform.toString(transform), transition, position: 'relative', zIndex: isDragging ? 1 : 'auto', opacity: isDragging ? 0.8 : 1 }}
    >
      {children({ attributes, listeners })}
    </Box>
  );
};

/**
 * The left side of the goals section: what is saved in all of them, what
 * went in this month, and every goal as a row with its bar and one line of
 * status. Picking a row opens the goal beside the list (or over it, on a
 * phone). Archived goals wait folded at the bottom.
 */
export const GoalList: React.FC<Props> = ({
  goals,
  archived,
  trails,
  selectedId,
  highlightSelected,
  onSelect,
  now,
  canCreate,
  onCreate,
  canReorder,
  onReorder,
  canModify,
  onUnarchive,
  onMenu,
}) => {
  const { t, i18n } = useTranslation('planning');
  const locale = i18n.language;
  const [showArchived, setShowArchived] = useState(false);
  const money = (value: number, currency: string) => formatMoney(value, currency, locale);
  const monthName = now.toLocaleDateString(locale, { month: 'long' });
  const today = localTodayIso(now);

  const totals = useMemo(
    () => sumByCurrency(goals.map((goal) => ({ amount: goal.currentAmount, currency: goal.currency }))),
    [goals],
  );

  // This month's deposits across the goals, and how many goals with a
  // deadline are keeping the pace they need.
  const { monthTotals, verdicts } = useMemo(() => {
    const key = monthKeyOf(now);
    const deposits: Array<{ amount: number; currency: string }> = [];
    let onTrack = 0;
    let judged = 0;
    goals.forEach((goal) => {
      const trail = trails[goal.id] ?? [];
      trail
        .filter((entry) => entry.kind !== 'adjustment' && entry.date.slice(0, 7) === key)
        .forEach((entry) => deposits.push({ amount: Number(entry.amount) || 0, currency: goal.currency }));
      const outlook = goal.goalType === 'target' ? goalOutlook(goal, trail, now) : null;
      if (outlook?.kind === 'path') {
        judged += 1;
        if (!outlook.behind) onTrack += 1;
      }
    });
    return { monthTotals: sumByCurrency(deposits), verdicts: { onTrack, judged } };
  }, [goals, trails, now]);

  const status = (goal: Goal): { text: string; tone: Tone } => {
    if (goal.isAchieved) return { text: t('goals.achieved'), tone: 'ok' };
    if (goal.goalType === 'recurring') {
      const target = Number(goal.monthlyTarget ?? 0);
      const contributed = Number(goal.monthContributed ?? 0);
      if (target <= 0) return { text: t('goals.list.accumulated', { amount: money(Number(goal.currentAmount), goal.currency) }), tone: 'muted' };
      if (contributed >= target) return { text: t('goals.monthComplete'), tone: 'ok' };
      return {
        text: t('goals.list.monthProgress', { month: capitalize(monthName), current: money(contributed, goal.currency), target: money(target, goal.currency) }),
        tone: 'muted',
      };
    }
    const outlook = goalOutlook(goal, trails[goal.id] ?? [], now);
    if (outlook?.kind === 'path') return outlook.behind ? { text: t('goals.list.behind'), tone: 'warn' } : { text: t('goals.list.onTrack'), tone: 'ok' };
    if (!goal.targetDate) return { text: t('goals.list.noDeadline'), tone: 'muted' };
    return goal.targetDate.slice(0, 10) < today
      ? { text: t('goals.list.deadlinePassed'), tone: 'warn' }
      : { text: t('goals.list.deadlineThisMonth'), tone: 'warn' };
  };

  // Dragging starts only after a small movement, so a tap still opens the goal.
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 6 } }));
  const onDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const from = goals.findIndex((goal) => goal.id === active.id);
    const to = goals.findIndex((goal) => goal.id === over.id);
    if (from < 0 || to < 0) return;
    onReorder(arrayMove(goals, from, to).map((goal) => goal.id));
  };

  return (
    <Box
      component='aside'
      aria-label={t('goals.list.label') as string}
      sx={{
        minWidth: 0,
        bgcolor: tokens.surface,
        border: `1px solid ${tokens.line}`,
        borderRadius: `${tokens.radius.card}px`,
        p: { xs: 1.5, md: 2 },
        // beside a long panel, the list stays in view while the goal scrolls
        ...(highlightSelected ? { position: 'sticky', top: 96, maxHeight: 'calc(100vh - 120px)', overflowY: 'auto' } : {}),
        display: 'flex',
        flexDirection: 'column',
        gap: 0.5,
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, px: 0.75, pt: 0.5, pb: 1.25 }}>
        <CardTitle sx={{ fontSize: 22, fontWeight: 700 }}>{t('goals.title')}</CardTitle>
        {canCreate && (
          <Button variant='contained' size='small' startIcon={<AddIcon />} onClick={onCreate} sx={{ minHeight: 36, px: 1.75 }}>
            {t('goals.add')}
          </Button>
        )}
      </Box>

      <Box sx={{ px: 0.75, pb: 1.5, mb: 0.5, borderBottom: `1px solid ${tokens.line}`, display: 'flex', flexDirection: 'column', gap: 0.25 }}>
        <Box component='span' sx={{ fontSize: 13, color: tokens.ink3 }}>{t('goals.totalSaved')}</Box>
        <Box component='span' sx={{ fontFamily: tokens.display, fontSize: 28, fontWeight: 700, letterSpacing: '-0.02em', fontVariantNumeric: 'tabular-nums', lineHeight: 1.2 }}>
          {totals.length ? totals.map(({ currency, total }) => money(total, currency)).join(' · ') : money(0, goals[0]?.currency ?? 'BRL')}
        </Box>
        <Box component='span' sx={{ fontSize: 13, color: tokens.ink3 }}>
          {t('goals.list.thisMonth', {
            amount: monthTotals.length ? monthTotals.map(({ currency, total }) => money(total, currency)).join(' · ') : money(0, goals[0]?.currency ?? 'BRL'),
            month: monthName,
          })}
          {verdicts.judged > 0 && ` · ${t('goals.list.onTrackCount', { count: verdicts.onTrack, total: verdicts.judged })}`}
        </Box>
      </Box>

      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
        <SortableContext items={goals.map((goal) => goal.id)} strategy={verticalListSortingStrategy}>
          <Box component='ul' sx={{ m: 0, p: 0, listStyle: 'none', display: 'flex', flexDirection: 'column', gap: 0.25 }}>
            {goals.map((goal) => {
              const color = goal.color || tokens.accent;
              const isHabit = goal.goalType === 'recurring';
              const progress = isHabit
                ? goalProgress(Number(goal.monthContributed ?? 0), Number(goal.monthlyTarget ?? 0))
                : goalProgress(Number(goal.currentAmount), Number(goal.targetAmount ?? 0));
              const hasBar = goal.isAchieved || !isHabit || Number(goal.monthlyTarget ?? 0) > 0;
              const selected = highlightSelected && goal.id === selectedId;
              const line = status(goal);
              return (
                <SortableRow key={goal.id} id={goal.id} disabled={!canReorder}>
                  {({ attributes, listeners }) => (
                    <Box
                      sx={{
                        position: 'relative',
                        borderRadius: '14px',
                        bgcolor: selected ? tokens.accentSoft : 'transparent',
                        '&:hover': { bgcolor: selected ? tokens.accentSoft : tokens.bg },
                        '&:hover .goal-drag, &:focus-within .goal-drag': { opacity: 1 },
                      }}
                    >
                      <ButtonBase
                        onClick={() => onSelect(goal)}
                        aria-current={selected ? 'true' : undefined}
                        sx={{
                          width: '100%',
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'stretch',
                          gap: 1,
                          px: 1.75,
                          pl: canReorder ? 3.5 : 1.75,
                          py: 1.75,
                          borderRadius: '14px',
                          textAlign: 'left',
                          font: 'inherit',
                          color: tokens.ink,
                          '&.Mui-focusVisible': { outline: `2px solid ${tokens.accent}`, outlineOffset: 2 },
                        }}
                      >
                        <Box component='span' sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                          <Box component='span' aria-hidden sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: color, flexShrink: 0 }} />
                          <Box component='span' sx={{ flex: 1, minWidth: 0, fontWeight: 700, fontSize: 15, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {goal.name}
                          </Box>
                          {hasBar && (
                            <Box component='span' sx={{ fontSize: 13, fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>
                              {`${Math.round(progress.ratio * 100)}%`}
                            </Box>
                          )}
                          {/* on narrow screens the goal opens in place of the list: say so */}
                          {!highlightSelected && <OpenIcon aria-hidden sx={{ fontSize: 20, color: tokens.ink3, mr: -0.75 }} />}
                        </Box>
                        {hasBar && (
                          <Box component='span' aria-hidden sx={{ display: 'block', height: 5, borderRadius: 99, bgcolor: tokens.line, overflow: 'hidden' }}>
                            <Box component='span' sx={{ display: 'block', height: '100%', width: `${progress.percent}%`, borderRadius: 99, bgcolor: color }} />
                          </Box>
                        )}
                        <Box component='span' sx={{ fontSize: 12.5, fontWeight: line.tone === 'muted' ? 400 : 600, color: toneColor[line.tone] }}>
                          {line.text}
                        </Box>
                      </ButtonBase>
                      {canReorder && (
                        <Box
                          className='goal-drag'
                          {...attributes}
                          {...listeners}
                          aria-label={t('goals.dragToReorder') as string}
                          sx={{
                            position: 'absolute',
                            left: 4,
                            top: 14,
                            display: 'flex',
                            cursor: 'grab',
                            touchAction: 'none',
                            color: tokens.ink3,
                            borderRadius: '6px',
                            '@media (hover: hover)': { opacity: 0 },
                            transition: 'opacity .15s ease',
                          }}
                        >
                          <DragIcon fontSize='small' />
                        </Box>
                      )}
                    </Box>
                  )}
                </SortableRow>
              );
            })}
          </Box>
        </SortableContext>
      </DndContext>

      {archived.length > 0 && (
        <Box sx={{ mt: 0.75, pt: 0.75, borderTop: `1px solid ${tokens.line}` }}>
          <ButtonBase
            onClick={() => setShowArchived((value) => !value)}
            aria-expanded={showArchived}
            sx={{ width: '100%', minHeight: 44, px: 1.75, borderRadius: '12px', display: 'flex', justifyContent: 'space-between', font: 'inherit', fontSize: 14, color: tokens.ink3, '&:hover': { bgcolor: tokens.bg } }}
          >
            <span>{t('goals.list.archived')}</span>
            <span>{archived.length}</span>
          </ButtonBase>
          <Collapse in={showArchived} unmountOnExit>
            <Box component='ul' aria-label={t('goals.list.archived') as string} sx={{ m: 0, p: 0, listStyle: 'none' }}>
              {archived.map((goal) => (
                <Box component='li' key={goal.id} sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 1.75, py: 1 }}>
                  <Box component='span' aria-hidden sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: goal.color || tokens.accent, opacity: 0.6, flexShrink: 0 }} />
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Box sx={{ fontSize: 14, fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{goal.name}</Box>
                    <Box sx={{ fontSize: 12.5, color: tokens.ink3 }}>
                      {money(Number(goal.currentAmount), goal.currency)}
                      {goal.targetAmount != null ? ` / ${money(Number(goal.targetAmount), goal.currency)}` : ''}
                    </Box>
                  </Box>
                  {canModify(goal) && (
                    <>
                      <Button size='small' variant='outlined' onClick={() => onUnarchive(goal)} sx={{ minHeight: 32, px: 1.25, fontSize: 12.5 }}>
                        {t('goals.menu.unarchive')}
                      </Button>
                      <IconButton size='small' aria-label={t('goals.rowMenu') as string} onClick={(event) => onMenu(event.currentTarget, goal)} sx={{ color: tokens.ink3 }}>
                        <MoreIcon fontSize='small' />
                      </IconButton>
                    </>
                  )}
                </Box>
              ))}
            </Box>
          </Collapse>
        </Box>
      )}
    </Box>
  );
};
